import React, { useMemo } from 'react';
import styled, {
    ThemeProvider as StyledComponentsThemeProvider,
    createGlobalStyle,
    css,
    DefaultTheme,
} from 'styled-components';
import { Text, TextProps } from 'rebass';
import { isMobile } from 'react-device-detect';
import { NavLink } from 'react-router-dom';
import Tooltip from 'components/TooltipInfo';
import { GradientTextColor } from './commonStyles';

const MEDIA_WIDTHS = {
    upToExtraSmall: 380,
    upToSmall: 720,
    upToMedium: 960,
    upToLarge: 1200,
    upToMoreLarge: 1440,
};

const mediaWidthTemplates: {
    [width in keyof typeof MEDIA_WIDTHS]: typeof css;
} = Object.keys(MEDIA_WIDTHS).reduce((accumulator, size) => {
    (accumulator as any)[size] = (a: any, b: any, c: any) => css`
        @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
            ${css(a, b, c)}
        }
    `;
    return accumulator;
}, {}) as any;

export function color(isDark: boolean) {
    return {
        // bg01: isDark ? '#10101C' : '#fff',
        // bg02: isDark ? '#313144' : '#f1f4f9',
        bg03: isDark ? '#10101C' : '#fff',
        bg04: isDark ? '#131324' : '#f9f9f9',
        bg05: isDark ? '#1C1C33' : '#fff',
        t01: isDark ? '#fff' : '#000',
        t02: 'rgba(0,0,0,0.6)',
        t03: '#07DDD0',
        t04: isDark ? '#07DDD0' : '#000',

        // TODO: add all new color, replace old color
        bg01: '#150701',
        bg02: isDark
            ? 'linear-gradient( 270deg, #BE692A 0%, #E8C074 100%)'
            : 'linear-gradient( 270deg, #BE692A 0%, #E8C074 100%)',
        mainColor: '#DCA45C',
        white01: '#ffffff',
        white02: '#fffaef',
        black01: '#0d0102',
        desc01: '#8c6437',

        linearGradientBg: 'linear-gradient(90deg, #f7931a 0%, #6a0dad 100%)',
        radialGradientBg:
            'radial-gradient(50% 50% at 50% 50%, rgba(247, 147, 26, 0.7) 0%, rgba(106, 13, 173, 0.4) 100%)',

        /**
         * gold: #F7931A
         */
        primary01: '#F7931A',

        /**
         * purple: #6A0DAD
         */
        primary02: '#6A0DAD',

        success: '#5fd15c',
        info: '#299DF5',
        warning: '#F7931A',
        error: '#FD2E2E',
        gray: '#585858',
    };
}

export function theme(darkMode: boolean): DefaultTheme {
    return {
        isDark: darkMode,
        mediaWidth: mediaWidthTemplates,
        c: color(darkMode),
        colors: {
            background: {
                primary: darkMode ? '#10101C' : '#fff',
                secondary: darkMode ? '#313144' : '#f1f4f9',
            },
            text: {
                primary: darkMode ? '#fff' : '#000',
                secondary: 'rgba(0,0,0,0.6)',
            },
            success: '#5fd15c',
        },
    };
}

export const ThemedGlobalStyle = createGlobalStyle`
.MuiTooltip-tooltip {
    padding: 12px;
    background: rgba(0,0,0,0.7);
    .MuiTooltip-arrow  {
        color: rgba(0,0,0,0.7);
    }
}
* {
    box-sizing: border-box;
    // font-family: "Poppins", sans-serif !important;
    ::-webkit-scrollbar {
        width: 8px;
        height: 100%;
        background-color: #1c1c1c;
        border-radius: 8px;
    }
    ::-webkit-scrollbar-thumb {
        background-color: #999;
        border-radius: 8px;
    }
    :focus-visible {
        outline: none;
    }
  }
  .grecaptcha-badge{
     display: none !important;
  }
`;

interface TypeProps extends TextProps {
    ellipsis?: boolean;
    wordBreak?: boolean;
    mobileFz?: number;
    shrink?: boolean;
    variant?: string;
}

const TextWrapper = styled(Text)<TypeProps>`
    color: ${({ color, theme }) => (theme.c as any)[color as string]};
    width: ${({ ellipsis, wordBreak }) => (ellipsis || wordBreak ? '100%' : 'unset')};
    overflow: ${({ ellipsis }) => (ellipsis ? 'hidden' : 'unset')};
    text-overflow: ${({ ellipsis }) => (ellipsis ? 'ellipsis' : 'unset')};
    white-space: ${({ ellipsis }) => (ellipsis ? 'nowrap' : 'unset')};
    word-break: ${({ wordBreak }) => (wordBreak ? 'break-all' : 'unset')};
    flex-shrink: ${({ shrink }) => (shrink ? 1 : 0)};
    ${(props) => (props.variant === 'title' ? GradientTextColor : undefined)};
`;

export const TYPE = {
    main(props: TypeProps) {
        const fontSize = isMobile && props.mobileFz ? props.mobileFz : props.fontSize;
        return <TextWrapper fontWeight={400} color="#fff" {...props} fontSize={fontSize || 16} />;
    },
    desc(props: TypeProps) {
        const fontSize = isMobile && props.mobileFz ? props.mobileFz : props.fontSize;
        return <TextWrapper fontWeight={400} color="#fff" {...props} fontSize={fontSize || 14} />;
    },
    title(props: TypeProps) {
        const fontSize = isMobile && props.mobileFz ? props.mobileFz : props.fontSize;
        return (
            <TextWrapper
                fontWeight={700}
                {...props}
                fontSize={fontSize || 16}
                variant="title"
                style={{ display: 'inline-block' }}
            />
        );
    },
};

export interface LinkProps {
    href: string;
    children: React.ReactNode;
    inner?: boolean;
    fontSize?: string;
    mbFrontSize?: string;
    color?: string;
    disableText?: string;
    className?: string;
}
const StyledLink = styled.a<LinkProps>`
    font-size: ${({ fontSize }) => fontSize || '16px'};
    font-weight: 400;
    color: ${({ color }) => color || '#040817'};
    line-height: 25px;
    text-decoration: underline;
    ${({ theme, mbFrontSize }) => theme.mediaWidth.upToSmall`
          font-size: ${mbFrontSize || '16px'} 
        `};
`;

const InnerLink = styled(NavLink)<LinkProps>`
    font-size: ${({ fontSize }) => fontSize || '16px'};
    font-weight: 400;
    color: ${({ color }) => color || '#040817'};
    line-height: 25px;
    text-decoration: underline;
    ${({ theme, mbFrontSize }) => theme.mediaWidth.upToSmall`
          fontSize: ${mbFrontSize || '16px'} 
        `};
`;
export const Link = (linkProps: LinkProps) => {
    const { href, children, inner, disableText } = linkProps;

    return disableText ? (
        <Tooltip title={disableText}>
            <StyledLink {...linkProps} href="javascript:;">
                {children}
            </StyledLink>
        </Tooltip>
    ) : inner ? (
        <InnerLink to={href} {...linkProps}>
            {children}
        </InnerLink>
    ) : (
        <StyledLink {...linkProps} target="_blank">
            {children}
        </StyledLink>
    );
};
